<!-- NOTICE: KAPTION-INTEGRATION -->
<script>
import Modal from 'dashboard/components/Modal.vue';

export default {
  name: 'KaptionKnowledgeBaseModal',
  components: {
    Modal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    // NOTICE: KAPTION-INTEGRATION - Support both single KB (backward compat) and array of KBs
    knowledgeBase: {
      type: Object,
      default: null,
    },
    knowledgeBases: {
      type: Array,
      default: null,
    },
  },
  emits: ['close'],
  data() {
    return {
      // NOTICE: KAPTION-INTEGRATION - Track selected knowledge base in split-pane view
      selectedKnowledgeBaseIndex: 0,
    };
  },
  computed: {
    // NOTICE: KAPTION-INTEGRATION - Normalize to array format for unified handling
    knowledgeBasesList() {
      if (this.knowledgeBases && this.knowledgeBases.length > 0) {
        return this.knowledgeBases;
      }
      if (this.knowledgeBase) {
        return [this.knowledgeBase];
      }
      return [];
    },
    // NOTICE: KAPTION-INTEGRATION - Get currently selected knowledge base
    currentKnowledgeBase() {
      if (this.knowledgeBasesList.length === 0) return null;
      return (
        this.knowledgeBasesList[this.selectedKnowledgeBaseIndex] ||
        this.knowledgeBasesList[0]
      );
    },
    // NOTICE: KAPTION-INTEGRATION - Check if we're in multi-source mode
    // Use split-pane when knowledgeBases prop is explicitly provided (from citation pill)
    // Use single layout when only knowledgeBase prop is provided (backward compatibility)
    isMultiSourceMode() {
      return this.knowledgeBases !== null && this.knowledgeBases !== undefined;
    },
    // NOTICE: KAPTION-INTEGRATION - Build highlighted content with spans
    highlightedContent() {
      const kb = this.currentKnowledgeBase;
      if (!kb?.content_snippet) {
        return '';
      }

      // If no spans data, just return the snippet as escaped HTML
      if (!kb?.content_spans || !kb?.spans_used?.length) {
        return this.escapeHtml(kb.content_snippet);
      }

      const content = kb.content_snippet;
      const spans = kb.content_spans;
      const spansUsed = new Set(kb.spans_used || []);

      // Get all used spans and sort by start position
      const usedSpans = spans
        .filter(span => spansUsed.has(span.span_id))
        .sort((a, b) => a.start_char - b.start_char);

      // Build array of content parts (text or highlighted)
      const parts = [];
      let lastEnd = 0;

      usedSpans.forEach(span => {
        // Add text before this span
        if (span.start_char > lastEnd) {
          const beforeText = content.substring(lastEnd, span.start_char);
          parts.push({ type: 'text', content: beforeText });
        }

        // Add highlighted span
        const spanText = content.substring(span.start_char, span.end_char);
        parts.push({
          type: 'highlight',
          content: spanText,
          spanId: span.span_id,
        });

        lastEnd = span.end_char;
      });

      // Add remaining text after last span
      if (lastEnd < content.length) {
        const afterText = content.substring(lastEnd);
        parts.push({ type: 'text', content: afterText });
      }

      // Build HTML string from parts
      return parts
        .map(part => {
          if (part.type === 'highlight') {
            const escapedText = this.escapeHtml(part.content);
            // NOTICE: KAPTION-INTEGRATION - Use data attribute for styling instead of inline classes
            // Inline Tailwind classes don't work reliably in sanitized HTML
            return `<mark class="kaption-highlighted-span" data-span-id="${this.escapeHtml(part.spanId)}">${escapedText}</mark>`;
          }
          return this.escapeHtml(part.content);
        })
        .join('');
    },
    relevancePercentage() {
      const kb = this.currentKnowledgeBase;
      if (!kb?.relevance_score) return 0;
      return Math.round(kb.relevance_score * 100);
    },
    // NOTICE: KAPTION-INTEGRATION - Translation computed properties
    modalTitle() {
      if (this.isMultiSourceMode) {
        return (
          this.$t('KNOWLEDGE_BASE_MODAL.SOURCES_TITLE', {
            count: this.knowledgeBasesList.length,
          }) || `Sources (${this.knowledgeBasesList.length})`
        );
      }
      return (
        this.currentKnowledgeBase?.knowledgebase_title ||
        this.$t('KNOWLEDGE_BASE_MODAL.TITLE')
      );
    },
    contentLabel() {
      return this.$t('KNOWLEDGE_BASE_MODAL.CONTENT_LABEL');
    },
    noContentText() {
      return this.$t('KNOWLEDGE_BASE_MODAL.NO_CONTENT');
    },
    highlightedText() {
      return this.$t('KNOWLEDGE_BASE_MODAL.HIGHLIGHTED');
    },
    highlightedDescription() {
      return this.$t('KNOWLEDGE_BASE_MODAL.HIGHLIGHTED_DESCRIPTION');
    },
  },
  watch: {
    show(newValue) {
      // Reset selection when modal opens
      if (newValue) {
        this.selectedKnowledgeBaseIndex = 0;
      }
    },
  },
  methods: {
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
    onClose() {
      this.$emit('close');
    },
    // NOTICE: KAPTION-INTEGRATION - Handle source selection in split-pane view
    onSourceSelect(index) {
      this.selectedKnowledgeBaseIndex = index;
    },
    // NOTICE: KAPTION-INTEGRATION - Get knowledge base display name
    getKnowledgeBaseName(kb) {
      if (!kb) return 'Unknown';
      if (typeof kb === 'string') {
        return kb;
      }
      return kb.knowledgebase_title || kb.knowledgebase_id || 'Unknown';
    },
    // NOTICE: KAPTION-INTEGRATION - Get relevance score for a KB
    getRelevanceScore(kb) {
      if (!kb?.relevance_score) return 0;
      return Math.round(kb.relevance_score * 100);
    },
  },
};
</script>

<template>
  <Modal :show="show" size="wide" @close="onClose">
    <!-- NOTICE: KAPTION-INTEGRATION - Split-pane layout for multiple sources -->
    <div
      v-if="isMultiSourceMode"
      class="flex h-[80vh] max-h-[80vh] overflow-hidden"
    >
      <!-- Left Panel: Sources List -->
      <div
        class="w-2/5 border-r border-slate-100 dark:border-slate-800 flex flex-col"
      >
        <!-- Header -->
        <div class="p-4 border-b border-slate-100 dark:border-slate-800">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ modalTitle }}
          </h2>
        </div>

        <!-- Sources List -->
        <div class="flex-1 overflow-y-auto">
          <button
            v-for="(kb, index) in knowledgeBasesList"
            :key="typeof kb === 'string' ? kb : kb.knowledgebase_id || index"
            class="w-full px-4 py-3 text-left border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
            :class="{
              'bg-woot-50 dark:bg-woot-900/20 border-l-4 border-l-woot-500':
                selectedKnowledgeBaseIndex === index,
            }"
            @click="onSourceSelect(index)"
          >
            <div
              class="font-medium text-sm text-slate-900 dark:text-slate-100 mb-1"
            >
              {{ getKnowledgeBaseName(kb) }}
            </div>
            <div class="text-xs text-slate-500 dark:text-slate-400">
              {{ getRelevanceScore(kb) }}%
              {{ $t('KNOWLEDGE_BASE_MODAL.RELEVANCE') || 'relevance' }}
            </div>
          </button>
        </div>
      </div>

      <!-- Right Panel: Content with Highlights -->
      <div class="flex-1 flex flex-col overflow-hidden">
        <!-- Header -->
        <div class="p-4 border-b border-slate-100 dark:border-slate-800">
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ getKnowledgeBaseName(currentKnowledgeBase) }}
          </h3>
          <div class="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {{ getRelevanceScore(currentKnowledgeBase) }}%
            {{ $t('KNOWLEDGE_BASE_MODAL.RELEVANCE') || 'relevance' }}
          </div>
        </div>

        <!-- Legend -->
        <div
          v-if="currentKnowledgeBase?.spans_used?.length"
          class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-100 dark:border-slate-800"
        >
          <div
            class="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400"
          >
            <mark class="kaption-highlighted-span">{{ highlightedText }}</mark>
            <span>{{ highlightedDescription }}</span>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-4">
          <div
            v-if="highlightedContent"
            v-dompurify-html="highlightedContent"
            class="text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap break-words"
          />
          <div v-else class="text-sm text-slate-500 dark:text-slate-400 italic">
            {{ noContentText }}
          </div>
        </div>
      </div>
    </div>

    <!-- NOTICE: KAPTION-INTEGRATION - Single source layout (backward compatibility) -->
    <div v-else class="p-6 max-h-[80vh] overflow-y-auto">
      <!-- Header -->
      <div class="mb-4">
        <h2
          class="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2"
        >
          {{ modalTitle }}
        </h2>
      </div>

      <!-- Legend - Moved to top -->
      <div
        v-if="currentKnowledgeBase?.spans_used?.length"
        class="mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
      >
        <div
          class="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400"
        >
          <mark class="kaption-highlighted-span">{{ highlightedText }}</mark>
          <span>{{ highlightedDescription }}</span>
        </div>
      </div>

      <!-- Content -->
      <div class="border-t border-slate-200 dark:border-slate-700 pt-4">
        <h3
          class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3"
        >
          {{ contentLabel }}
        </h3>
        <div
          v-if="highlightedContent"
          v-dompurify-html="highlightedContent"
          class="text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap break-words"
        />
        <div v-else class="text-sm text-slate-500 dark:text-slate-400 italic">
          {{ noContentText }}
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
/* NOTICE: KAPTION-INTEGRATION - Highlighted span styling */
/* Ensure mark elements are properly styled for both light and dark modes */
:deep(mark.kaption-highlighted-span),
:deep(.kaption-highlighted-span) {
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  /* Light mode: bright yellow background */
  background-color: rgb(254 240 138); /* yellow-200 */
  color: rgb(113 63 18); /* yellow-900 text for contrast */
}

/* Dark mode: brighter yellow that's visible on dark backgrounds */
@media (prefers-color-scheme: dark) {
  :deep(mark.kaption-highlighted-span),
  :deep(.kaption-highlighted-span) {
    background-color: rgb(
      250 204 21
    ); /* yellow-400 - bright enough for dark mode */
    color: rgb(0 0 0); /* black text for contrast on bright yellow */
  }
}

/* Fallback for dark mode when using class-based dark mode (not just prefers-color-scheme) */
:deep(.dark mark.kaption-highlighted-span),
:deep(.dark .kaption-highlighted-span) {
  background-color: rgb(250 204 21); /* yellow-400 */
  color: rgb(0 0 0); /* black text */
}
</style>
